import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api, { todayStr, formatDate } from '../lib/api';
import { AiChatWidget } from '../components/AiChatWidget';
import { validTimeOptionsForDate } from '../components/ReservationDialogs';
import { ChevronRight, ChevronLeft, Clock, AlertTriangle, Users, Check, Info, CalendarDays } from 'lucide-react';

// Convert Drive/Dropbox share URLs to direct image URLs (mirrors BrandLogo)
function toDirectImg(url) {
  if (!url || typeof url !== 'string') return url;
  const m1 = url.match(/drive\.google\.com\/file\/d\/([^/]+)\//);
  if (m1) return `https://drive.google.com/uc?export=view&id=${m1[1]}`;
  const m2 = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
  if (m2) return `https://drive.google.com/uc?export=view&id=${m2[1]}`;
  if (url.includes('dropbox.com') && url.includes('dl=0')) return url.replace('dl=0', 'raw=1');
  return url;
}


function addMinutesToHm(hm, minutes) {
  if (!hm) return '';
  const [h, m] = hm.split(':').map(Number);
  const total = h * 60 + m + (Number(minutes) || 0);
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
}


export default function CustomerBooking({ embed = false } = {}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [persons, setPersons] = useState(2);
  const [date, setDate] = useState(todayStr());
  const [areas, setAreas] = useState([]);
  const [preferredArea, setPreferredArea] = useState('');
  const [settings, setSettings] = useState(null);
  const [logoImgFailed, setLogoImgFailed] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offerQty, setOfferQty] = useState({});  // offer_id -> quantity

  const [calendarDays, setCalendarDays] = useState([]);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState({ customer_name: '', phone: '', email: '', note: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(null);  // guest-chosen stay (minutes)
  const [submitting, setSubmitting] = useState(false);

  const [showWaitlist, setShowWaitlist] = useState(false);
  const [openingHours, setOpeningHours] = useState([]);
  const [specialDays, setSpecialDays] = useState([]);
  const [publicEvents, setPublicEvents] = useState([]);

  useEffect(() => {
    api.get('/areas/public').then(r => setAreas(r.data)).catch(() => {});
    api.get('/settings/public').then(r => setSettings(r.data)).catch(() => {});
    api.get('/tags/public').then(r => setTags(r.data)).catch(() => setTags([]));
    api.get('/offers').then(r => setOffers(r.data || [])).catch(() => setOffers([]));
    api.get('/opening-hours/all').then(r => setOpeningHours(r.data || [])).catch(() => setOpeningHours([]));
    api.get('/special-days').then(r => setSpecialDays(r.data || [])).catch(() => setSpecialDays([]));
    api.get('/public/events').then(r => setPublicEvents(r.data?.events || [])).catch(() => setPublicEvents([]));
  }, []);

  useEffect(() => {
    api.get(`/availability/calendar?year=${calMonth.year}&month=${calMonth.month}&persons=${persons}`)
      .then(r => setCalendarDays(r.data))
      .catch(() => setCalendarDays([]));
  }, [calMonth, persons]);

  // Max. online buchbare Personen für das gewählte Datum (Wochentag-Override möglich).
  const effectiveMaxPersons = (() => {
    const base = settings?.max_online_persons || 12;
    const byWd = settings?.max_online_persons_by_weekday || {};
    try {
      const jsDay = new Date(date + 'T00:00:00').getDay(); // 0=So..6=Sa
      const wd = String((jsDay + 6) % 7);                  // -> 0=Mo..6=So
      const v = byWd[wd];
      if (v != null && Number(v) > 0) return Number(v);
    } catch { /* ignore */ }
    return base;
  })();

  useEffect(() => {
    if (persons > effectiveMaxPersons) setPersons(effectiveMaxPersons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMaxPersons]);

  const loadSlots = async () => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    try {
      const r = await api.post('/availability', {
        date,
        persons,
        preferred_area_id: preferredArea || null,
        duration_minutes: (settings?.guest_duration_enabled && selectedDuration) ? selectedDuration : null,
      });
      setSlots(r.data.slots || []);
      if (r.data.message) toast.info(r.data.message);
    } catch (e) {
      toast.error('Fehler beim Laden der Zeiten');
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Guest-selectable stay duration options for the selected date's weekday.
  const durationOptions = useMemo(() => {
    if (!settings?.guest_duration_enabled || !date) return [];
    const wd = (new Date(date + 'T00:00:00').getDay() + 6) % 7;  // 0=Mo … 6=So
    const rule = (settings.guest_duration_options || []).find(r => r.weekday === wd);
    return [...new Set((rule?.durations || []).filter(d => Number(d) > 0).map(Number))].sort((a, b) => a - b);
  }, [settings, date]);

  useEffect(() => {
    if (durationOptions.length && !durationOptions.includes(selectedDuration)) {
      setSelectedDuration(durationOptions[0]);
    } else if (!durationOptions.length && selectedDuration != null) {
      setSelectedDuration(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationOptions]);

  useEffect(() => {
    if (step === 2 && date && persons) loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, date, persons, preferredArea, selectedDuration]);

  // Auto-refresh slots every minute when the booking is for today, so already
  // passed times disappear without a manual reload
  useEffect(() => {
    if (step !== 2 || !date) return;
    if (date !== todayStr()) return;
    const id = setInterval(() => {
      loadSlots();
    }, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, date, persons, preferredArea, selectedDuration]);

  const submit = async () => {
    if (!form.customer_name || !form.phone) {
      toast.error('Bitte Name und Telefon ausfüllen');
      return;
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Bitte eine gültige E-Mail-Adresse angeben');
      return;
    }
    if (settings?.privacy_checkbox_required && !privacyAccepted) {
      toast.error('Bitte bestätigen Sie den Datenschutzhinweis, bevor Sie die Reservierung absenden.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        note: settings?.notes_field_enabled ? form.note : '',
        date,
        start_time: selectedSlot.time,
        persons,
        preferred_area_id: preferredArea || null,
        is_short_slot: selectedSlot.is_short_slot,
        // Aufenthaltsdauer nie über die Schließung hinaus: auf das (bereits
        // schließ-/kapazitätsbegrenzte) max_stay_minutes des Slots deckeln.
        duration_minutes: (settings?.guest_duration_enabled && selectedDuration)
          ? Math.min(selectedDuration, selectedSlot.max_stay_minutes ?? selectedSlot.max_duration_minutes)
          : selectedSlot.max_duration_minutes,
        combination_id: selectedSlot.combination_id || null,
        tag_ids: selectedTagIds,
        offers: Object.entries(offerQty)
          .filter(([, q]) => q > 0)
          .map(([id, q]) => ({ offer_id: id, quantity: q })),
        privacy_accepted: privacyAccepted,
      };
      const r = await api.post('/reservations', payload);
      // Notify parent window if embedded — allows host site to close modal or scroll to top
      if (embed && window.parent !== window) {
        try {
          window.parent.postMessage({
            type: 'lacontadina:reservation-created',
            reservation_id: r.data.id,
            status: r.data.status,
          }, '*');
        } catch {/* noop */}
      }
      navigate(`/confirmation/${r.data.id}${embed ? '?embed=1' : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Reservierung');
    } finally {
      setSubmitting(false);
    }
  };

  // Preload the hero image as soon as we know its URL so the user never sees
  // an empty hero. The browser kicks off the request before React renders the
  // CSS background, eliminating most of the visual delay.
  useEffect(() => {
    if (!settings?.booking_image_url) return;
    const img = new Image();
    img.src = toDirectImg(settings.booking_image_url);
  }, [settings?.booking_image_url]);

  return (
    <div className={`${embed ? '' : 'min-h-screen'} bg-[#F9F9F7]`} data-testid="customer-booking-page">
      {/* Hero header — hidden in embed mode */}
      {!embed && (
      <div
        className="hero-image min-h-[44vh] md:min-h-[52vh] relative bg-cover bg-center bg-[#1C1C19]"
        style={settings?.booking_image_url ? { backgroundImage: `linear-gradient(180deg, rgba(28,28,25,0) 0%, rgba(28,28,25,0.0) 50%, rgba(28,28,25,0.85) 100%), url("${toDirectImg(settings.booking_image_url)}")` } : {}}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1C19]/30 via-transparent to-[#1C1C19]/80"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-8 md:pt-12 pb-16 md:pb-20 flex flex-col h-full min-h-[44vh] md:min-h-[52vh]">
          <div className="flex items-center justify-between text-white" data-testid="customer-hero-top">
            <div
              className={`flex items-center gap-3 transition-opacity duration-500 ${settings ? 'opacity-100' : 'opacity-0'}`}
              data-testid="customer-logo"
            >
              {settings?.logo_url && !logoImgFailed ? (
                <img
                  src={toDirectImg(settings.logo_url)}
                  alt={settings?.logo_text || 'Logo'}
                  onError={() => setLogoImgFailed(true)}
                  className="h-8 md:h-10 w-auto object-contain"
                />
              ) : (
                <span className="font-brand text-3xl md:text-4xl tracking-tight">
                  {settings?.logo_text || settings?.restaurant_name || ''}<span className="text-[#EAB308]">.</span>
                </span>
              )}
            </div>
          </div>
          <div className={`mt-auto text-white transition-opacity duration-500 ${settings ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-sm md:text-base uppercase tracking-[0.25em] text-white/80 mb-3">Tischreservierung</p>
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl leading-snug max-w-2xl text-white/95">
              Ein Platz für Sie – schnell und einfach reserviert.
            </h1>
            {settings?.restaurant_name && (
              <p className="mt-4 text-base md:text-lg text-white/90 font-light max-w-xl">
                {settings.restaurant_name}
              </p>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Booking card */}
      <div className={`max-w-3xl mx-auto px-4 md:px-6 ${embed ? 'pt-4 pb-6' : '-mt-14 md:-mt-16 pb-20'} relative z-20`}>
        {!embed && publicEvents.length > 0 && (
          <button
            type="button"
            onClick={() => navigate('/events')}
            data-testid="events-teaser"
            className="w-full mb-4 bg-[#1C1C19] text-white rounded-lg p-4 md:p-5 flex items-center justify-between gap-3 shadow-md hover:bg-[#2A2A26] transition-colors animate-fade-up"
          >
            <span className="flex items-center gap-3 text-left">
              <CalendarDays className="w-5 h-5 md:w-6 md:h-6 text-[#EAB308] flex-shrink-0" />
              <span>
                <span className="block font-medium text-sm md:text-base">Besondere Veranstaltungen buchbar</span>
                <span className="block text-xs md:text-sm text-white/70">{publicEvents.length} {publicEvents.length === 1 ? 'Event' : 'Events'} – jetzt Plätze sichern</span>
              </span>
            </span>
            <ChevronRight className="w-5 h-5 text-[#EAB308] flex-shrink-0" />
          </button>
        )}
        {(() => {
          const todayISO = new Date().toISOString().slice(0, 10);
          const noticeText = (settings?.booking_notice_text || '').trim();
          const nf = settings?.booking_notice_from || '';
          const nt = settings?.booking_notice_to || '';
          const showNotice = !!settings?.booking_notice_enabled && !!noticeText
            && (!nf || todayISO >= nf) && (!nt || todayISO <= nt);
          if (!showNotice) return null;
          return (
            <div
              data-testid="booking-notice"
              className="mb-4 bg-[#FEF3C7] border-2 border-[#EAB308] rounded-lg p-4 md:p-5 flex items-start gap-3 shadow-md animate-fade-up"
            >
              <Info className="w-5 h-5 md:w-6 md:h-6 text-[#B45309] flex-shrink-0 mt-0.5" />
              <p className="text-sm md:text-base text-[#7C2D12] font-medium whitespace-pre-line leading-relaxed">
                {noticeText}
              </p>
            </div>
          );
        })()}
        <div className={`bg-white ${embed ? '' : 'border border-[#E5E5E0] shadow-lg'} rounded-lg p-6 md:p-10 animate-fade-up`}>
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-8 text-xs uppercase tracking-widest">
            <span className={`px-2 py-1 ${step >= 1 ? 'text-[#1C1C19]' : 'text-[#9CA3AF]'}`}>1. Zeit</span>
            <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
            <span className={`px-2 py-1 ${step >= 2 ? 'text-[#1C1C19]' : 'text-[#9CA3AF]'}`}>2. Uhrzeit</span>
            <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
            <span className={`px-2 py-1 ${step >= 3 ? 'text-[#1C1C19]' : 'text-[#9CA3AF]'}`}>3. Kontakt</span>
          </div>

          {step === 1 && (
            <StepDateAndPersons
              persons={persons} setPersons={setPersons}
              maxPersons={effectiveMaxPersons}
              date={date} setDate={setDate}
              areas={areas} preferredArea={preferredArea} setPreferredArea={setPreferredArea}
              calMonth={calMonth} setCalMonth={setCalMonth}
              calendarDays={calendarDays}
              specialDays={specialDays}
              durationOptions={durationOptions}
              selectedDuration={selectedDuration}
              setSelectedDuration={setSelectedDuration}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <StepTimeSlot
              date={date} persons={persons}
              slots={slots} loading={loadingSlots}
              selectedSlot={selectedSlot} setSelectedSlot={setSelectedSlot}
              selectedDuration={settings?.guest_duration_enabled ? selectedDuration : null}
              onBack={() => setStep(1)}
              onNext={() => selectedSlot && setStep(3)}
              onRefresh={loadSlots}
              onWaitlist={settings?.waitlist_enabled ? () => setShowWaitlist(true) : null}
              preferredArea={preferredArea}
              setPreferredArea={setPreferredArea}
              areas={areas}
              isClosedDay={calendarDays.some(d => d.date === date && !d.open)}
              closedNotice={(specialDays.find(d => d.date === date && d.type === 'closed' && d.guest_notice_enabled && d.guest_notice_text) || {}).guest_notice_text || ''}
            />
          )}
          {step === 3 && (
            <StepContact
              form={form} setForm={setForm}
              date={date} persons={persons} slot={selectedSlot}
              tags={tags}
              selectedTagIds={selectedTagIds}
              setSelectedTagIds={setSelectedTagIds}
              offers={offers}
              offerQty={offerQty}
              setOfferQty={setOfferQty}
              notesEnabled={settings?.notes_field_enabled}
              notesMaxChars={settings?.notes_field_max_chars || 200}
              selectedDuration={settings?.guest_duration_enabled ? selectedDuration : null}
              privacyAccepted={privacyAccepted}
              setPrivacyAccepted={setPrivacyAccepted}
              privacyRequired={settings?.privacy_checkbox_required}
              submitting={submitting}
              onBack={() => setStep(2)}
              onSubmit={submit}
            />
          )}
        </div>
        {/* Footer with legal links */}
        <footer className="max-w-md mx-auto mt-10 mb-4 px-4 text-center text-xs text-[#5C5C59]" data-testid="customer-footer">
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer" data-testid="footer-datenschutz" className="hover:text-[#1C1C19] underline">Datenschutz für Reservierungen</a>
          <span className="mx-2">·</span>
          <a href="/impressum" target="_blank" rel="noopener noreferrer" data-testid="footer-impressum" className="hover:text-[#1C1C19] underline">Impressum</a>
        </footer>
      </div>

      <WaitlistDialog
        open={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        date={date}
        persons={persons}
        defaultTime={selectedSlot?.time || ''}
        areas={areas}
        preferredArea={preferredArea}
        openingHours={openingHours}
        specialDays={specialDays}
        settings={settings}
      />

      <AiChatWidget
        enabled={settings?.ai_assistant_enabled}
        restaurantName={settings?.logo_text || settings?.restaurant_name}
      />
    </div>
  );
}

function StepDateAndPersons({ persons, setPersons, maxPersons = 12, date, setDate, areas, preferredArea, setPreferredArea, calMonth, setCalMonth, calendarDays, specialDays = [], durationOptions, selectedDuration, setSelectedDuration, onNext }) {
  const personOptions = Array.from({ length: Math.max(1, maxPersons) }, (_, i) => i + 1);
  const closedSet = new Set(calendarDays.filter(d => !d.open).map(d => d.date));
  const selectedIsClosed = !!date && closedSet.has(date);
  const selectedClosedNotice = (specialDays.find(d => d.date === date && d.type === 'closed' && d.guest_notice_enabled && d.guest_notice_text) || {}).guest_notice_text || '';
  const fmtDuration = (min) => {
    const h = Math.floor(min / 60), m = min % 60;
    if (h && m) return `${h}:${String(m).padStart(2, '0')} Std`;
    if (h) return `${h} Std`;
    return `${m} Min`;
  };
  const bookableAreas = (areas || []).filter(a => a.online_bookable !== false);

  // Auto-select when there is exactly one bookable area (nothing to choose).
  useEffect(() => {
    if (bookableAreas.length === 1 && !preferredArea) {
      setPreferredArea(bookableAreas[0].id);
    }
  }, [bookableAreas, preferredArea, setPreferredArea]);

  const monthName = new Date(calMonth.year, calMonth.month - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    let { year, month } = calMonth;
    month -= 1; if (month < 1) { month = 12; year -= 1; }
    setCalMonth({ year, month });
  };
  const nextMonth = () => {
    let { year, month } = calMonth;
    month += 1; if (month > 12) { month = 1; year += 1; }
    setCalMonth({ year, month });
  };

  // Build calendar grid
  const firstDay = new Date(calMonth.year, calMonth.month - 1, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7; // 0 = Monday
  const daysInMonth = new Date(calMonth.year, calMonth.month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayS = todayStr();

  return (
    <div data-testid="step-date-and-persons">
      <h2 className="font-display text-2xl md:text-3xl text-[#1C1C19] mb-2">Wann möchten Sie kommen?</h2>
      <p className="text-[#5C5C59] mb-8">Wählen Sie Personenzahl und Datum.</p>

      <div className="mb-8">
        <label className="block text-sm font-medium text-[#1C1C19] mb-3">Anzahl Personen</label>
        <div className="grid grid-cols-6 gap-2" data-testid="person-grid">
          {personOptions.map(n => (
            <button key={n}
              onClick={() => setPersons(n)}
              data-testid={`person-option-${n}`}
              className={`py-3 rounded border text-sm font-medium transition-all ${persons === n ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'bg-white text-[#1C1C19] border-[#E5E5E0] hover:border-[#1C1C19]'}`}
            >{n}</button>
          ))}
        </div>
        <p className="text-xs text-[#5C5C59] mt-2">Für größere Gruppen kontaktieren Sie uns bitte telefonisch.</p>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-[#1C1C19]">Datum</label>
          <div className="flex items-center gap-3">
            <button onClick={prevMonth} className="p-1 hover:bg-[#F9F9F7] rounded" data-testid="cal-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium capitalize w-32 text-center" data-testid="cal-month-label">{monthName}</span>
            <button onClick={nextMonth} className="p-1 hover:bg-[#F9F9F7] rounded" data-testid="cal-next-month">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs text-center text-[#5C5C59] mb-2">
          {['Mo','Di','Mi','Do','Fr','Sa','So'].map(d => <div key={d} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
          {cells.map((d, idx) => {
            if (!d) return <div key={idx} />;
            const ds = `${calMonth.year}-${String(calMonth.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const isPast = ds < todayS;
            const isClosed = closedSet.has(ds);
            const isSelected = ds === date;
            const disabled = isPast;
            return (
              <button key={idx}
                disabled={disabled}
                onClick={() => setDate(ds)}
                data-testid={`cal-day-${ds}`}
                title={isClosed && !isPast ? 'Geschlossen – für Infos antippen' : undefined}
                className={`aspect-square text-sm rounded transition-all relative ${
                  isSelected ? 'bg-[#1C1C19] text-white' :
                  disabled ? 'text-[#9CA3AF] cursor-not-allowed' :
                  isClosed ? 'text-[#9CA3AF] line-through hover:bg-[#FEF3C7] border border-transparent hover:border-[#FCD34D]' :
                  'text-[#1C1C19] hover:bg-[#F9F9F7] border border-transparent hover:border-[#E5E5E0]'
                }`}
              >
                {d}
                {isClosed && !isPast && <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-[#EF4444]'}`}></span>}
              </button>
            );
          })}
        </div>
        {selectedIsClosed && (
          <div className="mt-4 flex items-start gap-3 bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-4 py-3" data-testid="calendar-closed-notice">
            <AlertTriangle className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#1C1C19] mb-0.5">An diesem Tag geschlossen</p>
              <p className="text-sm text-[#78350F] whitespace-pre-line">
                {selectedClosedNotice || 'An diesem Datum haben wir Ruhetag. Bitte wählen Sie ein anderes Datum.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {!selectedIsClosed && (
      <>
      {/* Guest-selectable stay duration (between date and area) */}
      {date && durationOptions && durationOptions.length > 0 && (
        <div className="mb-8" data-testid="duration-section">
          <label className="block text-sm font-medium text-[#1C1C19] mb-1">Wie lange möchten Sie bleiben?</label>
          <p className="text-xs text-[#5C5C59] mb-3">Wählen Sie Ihre gewünschte Aufenthaltsdauer.</p>
          <div className="flex flex-wrap gap-2">
            {durationOptions.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setSelectedDuration(d)}
                data-testid={`duration-option-${d}`}
                className={`px-4 py-2 rounded-full border text-sm transition-colors ${selectedDuration === d ? 'bg-[#1C1C19] border-[#1C1C19] text-white' : 'bg-white border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'}`}
              >
                {fmtDuration(d)}
              </button>
            ))}
          </div>
        </div>
      )}

      {bookableAreas.length > 0 && (
        <div className="mb-8">
          <label className="block text-sm font-medium text-[#1C1C19] mb-3">
            Bereich wählen <span className="text-[#EF4444]">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="area-options">
            {bookableAreas.map(a => {
              const selected = preferredArea === a.id;
              return (
                <button key={a.id}
                  onClick={() => setPreferredArea(a.id)}
                  data-testid={`area-option-${a.id}`}
                  aria-pressed={selected}
                  className={`relative aspect-video w-full rounded-xl overflow-hidden border-2 text-left transition-all duration-300 group ${selected ? 'border-[#1C1C19] ring-2 ring-[#1C1C19]/20 shadow-lg' : 'border-[#E5E5E0] hover:border-[#1C1C19] hover:shadow-md'}`}
                >
                  {a.image_url ? (
                    <img src={a.image_url} alt={a.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${a.color}, ${a.color}bb)` }} />
                  )}
                  {/* Textverlauf für Lesbarkeit */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                  {selected && (
                    <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#1C1C19] text-white flex items-center justify-center shadow-lg" data-testid={`area-selected-${a.id}`}>
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="text-white font-medium text-lg leading-tight drop-shadow-md">{a.name}</div>
                    {a.description && <div className="text-white/85 text-xs mt-0.5 line-clamp-2 drop-shadow">{a.description}</div>}
                  </div>
                </button>
              );
            })}
          </div>
          {!preferredArea && (
            <p className="text-xs text-[#9CA3AF] mt-2" data-testid="area-required-hint">Bitte wählen Sie einen Bereich, um fortzufahren.</p>
          )}
        </div>
      )}
      </>
      )}

      <button
        onClick={onNext}
        disabled={selectedIsClosed || (bookableAreas.length > 0 && !preferredArea)}
        data-testid="step1-next-btn"
        className="btn-primary w-full py-4 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Verfügbare Zeiten anzeigen
      </button>
    </div>
  );
}

function StepTimeSlot({ date, persons, slots, loading, selectedSlot, setSelectedSlot, selectedDuration, onBack, onNext, onRefresh, onWaitlist, preferredArea, setPreferredArea, areas, isClosedDay, closedNotice }) {
  // Effektive Aufenthaltsdauer eines Slots: die vom Gast gewünschte Dauer, aber
  // NIE länger als bis zur Schließung (slot.max_stay_minutes ist bereits durch
  // Schließzeit/Peak/freies Fenster begrenzt). So kann z. B. um 20:00 bei
  // Schließung 22:00 maximal bis 22:00 (2 Std.) gewählt werden – nicht 3 Std.
  const effDuration = (slot) => {
    const pref = selectedDuration || slot?.max_duration_minutes;
    const cap = slot?.max_stay_minutes ?? slot?.max_duration_minutes;
    return cap ? Math.min(pref, cap) : pref;
  };
  const selectedAreaName = (areas || []).find(a => a.id === preferredArea)?.name || '';
  const otherAreas = (areas || []).filter(a => a.id !== preferredArea && a.online_bookable !== false);
  // Waitlist makes no sense on closed days — restaurant simply won't open.
  const waitlistEnabled = !!onWaitlist && !isClosedDay;
  return (
    <div data-testid="step-time-slot">
      <button onClick={onBack} className="text-sm text-[#5C5C59] hover:text-[#1C1C19] mb-4 flex items-center gap-1" data-testid="step2-back-btn">
        <ChevronLeft className="w-4 h-4" /> Zurück
      </button>
      <h2 className="font-display text-2xl md:text-3xl text-[#1C1C19] mb-2">Verfügbare Uhrzeiten</h2>
      <p className="text-[#5C5C59] mb-6">
        {formatDate(date)} · {persons} {persons === 1 ? 'Person' : 'Personen'}
        {selectedAreaName && <> · Bereich: <span className="font-medium text-[#1C1C19]">{selectedAreaName}</span></>}
      </p>

      {loading ? (
        <div className="py-10 text-center text-[#5C5C59]">Lädt verfügbare Zeiten...</div>
      ) : slots.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-[#E5E5E0] rounded" data-testid="no-slots-empty-state">
          <AlertTriangle className="w-8 h-8 text-[#EAB308] mx-auto mb-3" />
          {isClosedDay ? (
            <>
              <p className="text-[#1C1C19] font-medium mb-1" data-testid="closed-day-headline">Restaurant an diesem Tag geschlossen</p>
              {closedNotice ? (
                <div className="text-sm text-[#B45309] bg-[#B45309]/10 border border-[#B45309]/20 rounded-lg px-4 py-3 mb-4 max-w-md mx-auto whitespace-pre-line" data-testid="closed-day-notice">
                  {closedNotice}
                </div>
              ) : (
                <p className="text-sm text-[#5C5C59] mb-4 max-w-md mx-auto">
                  An diesem Datum haben wir Ruhetag. Bitte wählen Sie ein anderes Datum.
                </p>
              )}
            </>
          ) : preferredArea ? (
            <>
              <p className="text-[#1C1C19] font-medium mb-1" data-testid="area-full-headline">
                Bereich „{selectedAreaName}" ist ausgebucht
              </p>
              <p className="text-sm text-[#5C5C59] mb-4 max-w-md mx-auto">
                An diesem Tag sind im gewählten Bereich keine Tische frei. Bitte wählen Sie einen anderen Bereich, ein anderes Datum, oder lassen Sie sich auf die Warteliste setzen.
              </p>
              {otherAreas.length > 0 && setPreferredArea && (
                <div className="flex flex-wrap justify-center gap-2 mb-3" data-testid="area-switch-buttons">
                  {otherAreas.slice(0, 4).map(a => (
                    <button
                      key={a.id}
                      onClick={() => setPreferredArea(a.id)}
                      className="btn-outline px-3 py-2 rounded text-xs"
                      data-testid={`area-switch-${a.id}`}
                    >
                      „{a.name}" versuchen
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-[#1C1C19] font-medium mb-1">Leider keine verfügbaren Zeiten</p>
              <p className="text-sm text-[#5C5C59] mb-4">An diesem Tag sind keine Tische frei. Bitte wählen Sie ein anderes Datum, oder lassen Sie sich auf die Warteliste setzen.</p>
            </>
          )}
          <div className="flex flex-wrap justify-center gap-2">
            <button onClick={onBack} className="btn-outline px-4 py-2 rounded text-sm" data-testid="step2-back-no-slots">Anderes Datum wählen</button>
            {waitlistEnabled && (
              <button onClick={onWaitlist} className="btn-primary px-4 py-2 rounded text-sm" data-testid="step2-waitlist-btn">Auf Warteliste setzen</button>
            )}
          </div>
        </div>
      ) : (
        <>
          {slots.some(s => s.wave_end) ? (
            <div className="space-y-5 mb-6" data-testid="time-slots-waves">
              {(() => {
                const groups = [];
                const idxMap = {};
                slots.forEach(s => {
                  const key = `${s.wave_name || ''}|${s.wave_end || ''}`;
                  if (idxMap[key] === undefined) { idxMap[key] = groups.length; groups.push({ name: s.wave_name, end: s.wave_end, items: [] }); }
                  groups[idxMap[key]].items.push(s);
                });
                return groups.map((g, gi) => (
                  <div key={gi} data-testid={`wave-group-${gi}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <h3 className="text-sm font-medium text-[#1C1C19]">{g.name || `Welle ${gi + 1}`}</h3>
                      {g.end && <span className="text-xs text-[#5C5C59]">Tisch bis <b className="text-[#1C1C19]">{g.end}</b> Uhr</span>}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {g.items.map((s, i) => (
                        <button key={i}
                          onClick={() => setSelectedSlot(s)}
                          data-testid={`time-slot-${s.time}`}
                          className={`slot-btn py-3 rounded text-sm font-medium ${selectedSlot?.time === s.time ? 'selected' : ''}`}
                        >
                          <div className="font-mono">{s.time}</div>
                          {s.combination_id && <div className="text-[10px] mt-1 uppercase tracking-wider opacity-90">Tischgruppe</div>}
                        </button>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-6" data-testid="time-slots-grid">
            {slots.map((s, i) => (
              <button key={i}
                onClick={() => setSelectedSlot(s)}
                data-testid={`time-slot-${s.time}`}
                className={`slot-btn py-3 rounded text-sm font-medium ${selectedSlot?.time === s.time ? 'selected' : ''} ${s.is_short_slot ? 'short' : ''}`}
              >
                <div className="font-mono">{s.time}</div>
                {s.is_short_slot && <div className="text-[10px] mt-1 uppercase tracking-wider opacity-90">Kurzzeit</div>}
                {s.combination_id && <div className="text-[10px] mt-1 uppercase tracking-wider opacity-90">Tischgruppe</div>}
                {s.shared_table && <div className="text-[10px] mt-1 uppercase tracking-wider opacity-90">Geteilter Tisch</div>}
              </button>
            ))}
          </div>
          )}

          {selectedSlot && (
            <div className="bg-[#1C1C19] text-white rounded p-4 mb-4 flex items-center justify-between" data-testid="selected-slot-range">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-1">Ihr Zeitrahmen</p>
                <p className="font-mono text-lg">
                  {selectedSlot.time} <span className="text-white/60 px-1">–</span> {addMinutesToHm(selectedSlot.time, effDuration(selectedSlot))}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-1">Aufenthalt</p>
                <p className="font-mono text-lg">{effDuration(selectedSlot)} Min.</p>
              </div>
            </div>
          )}

          {selectedSlot?.combination_id && (
            <div className="bg-[#EFF6FF] border border-[#3B82F6] rounded p-4 mb-6 flex gap-3" data-testid="combo-slot-info">
              <Users className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-[#1C1C19]">Sie werden auf einer Tischgruppe sitzen.</p>
                <p className="text-[#5C5C59] mt-1">
                  Für Ihre Gruppe wird eine Kombination mehrerer Tische bereit und zusammen gestellt.
                </p>
              </div>
            </div>
          )}

          {selectedSlot?.is_short_slot && (
            <div className="bg-[#FFFBEB] border border-[#EAB308] rounded p-4 mb-6 flex gap-3" data-testid="short-slot-warning">
              <Clock className="w-5 h-5 text-[#EAB308] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-[#1C1C19]">Dieser Tisch ist nur kürzer verfügbar.</p>
                <p className="text-[#5C5C59] mt-1">
                  Aufenthaltsdauer: {selectedSlot.max_duration_minutes} Minuten. Der Tisch wird danach für die nächste Reservierung benötigt.
                </p>
              </div>
            </div>
          )}

          {selectedSlot?.shared_table && (
            <div className="bg-[#F5F3FF] border border-[#9333EA] rounded p-4 mb-6 flex gap-3" data-testid="shared-slot-info">
              <Users className="w-5 h-5 text-[#9333EA] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-[#1C1C19]">Geteilter Tisch (Gemeinschaftstisch).</p>
                <p className="text-[#5C5C59] mt-1">
                  Zu dieser Zeit teilen Sie sich einen größeren Tisch mit weiteren Gästen. Ihre Plätze sind fest für Sie reserviert.
                </p>
              </div>
            </div>
          )}

          <button onClick={onNext} disabled={!selectedSlot} data-testid="step2-next-btn" className="btn-primary w-full py-4 rounded font-medium">
            Weiter zur Kontaktangabe
          </button>
          {waitlistEnabled && (
            <button onClick={onWaitlist} data-testid="step2-waitlist-link" className="w-full mt-3 text-sm text-[#5C5C59] hover:text-[#1C1C19] underline">
              Wunschzeit nicht dabei? Auf Warteliste setzen
            </button>
          )}
        </>
      )}
    </div>
  );
}

const CATEGORY_LABELS = {
  occasion: 'Besonderer Anlass',
  family: 'Familie',
  seating: 'Platzwünsche',
  service: 'Service',
  vip: 'VIP',
  other: 'Weitere Wünsche',
};

function StepContact({ form, setForm, date, persons, slot, tags, selectedTagIds, setSelectedTagIds, offers, offerQty, setOfferQty, notesEnabled, notesMaxChars, selectedDuration, privacyAccepted, setPrivacyAccepted, privacyRequired, submitting, onBack, onSubmit }) {
  // Aufenthaltsdauer in der Zusammenfassung nie über die Schließung hinaus
  // (auf das schließ-/kapazitätsbegrenzte max_stay_minutes des Slots deckeln).
  const summaryCap = slot?.max_stay_minutes ?? slot?.max_duration_minutes;
  const summaryDuration = Math.min(selectedDuration || slot?.max_duration_minutes || 0, summaryCap || Infinity) || (selectedDuration || slot?.max_duration_minutes);
  const toggleTag = (id) => {
    setSelectedTagIds(selectedTagIds.includes(id)
      ? selectedTagIds.filter(x => x !== id)
      : [...selectedTagIds, id]);
  };

  // Offers available for this party size
  const availOffers = (offers || []).filter(o =>
    o.active !== false &&
    persons >= (o.min_persons || 1) &&
    (o.max_persons == null || persons <= o.max_persons)
  );
  const setQty = (id, q) => setOfferQty({ ...offerQty, [id]: Math.max(0, q) });
  const fmtPrice = (p) => `${(Number(p) || 0).toFixed(2).replace('.', ',')} €`;
  const offerTotal = availOffers.reduce((sum, o) => sum + (offerQty[o.id] || 0) * (Number(o.price) || 0), 0);


  // Group active tags by category, preserving server order
  const grouped = {};
  for (const t of tags || []) {
    if (!t.active) continue;
    (grouped[t.category] = grouped[t.category] || []).push(t);
  }
  const orderedCats = ['occasion', 'family', 'seating', 'service', 'vip', 'other'].filter(c => grouped[c]?.length);

  return (
    <div data-testid="step-contact">
      <button onClick={onBack} className="text-sm text-[#5C5C59] hover:text-[#1C1C19] mb-4 flex items-center gap-1" data-testid="step3-back-btn">
        <ChevronLeft className="w-4 h-4" /> Zurück
      </button>
      <h2 className="font-display text-2xl md:text-3xl text-[#1C1C19] mb-2">Ihre Kontaktdaten</h2>
      <p className="text-[#5C5C59] mb-6">Wir bestätigen Ihre Reservierung in Kürze.</p>

      <div className="bg-[#F9F9F7] border border-[#E5E5E0] rounded p-4 mb-6 text-sm grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="reservation-summary">
        <div><div className="text-[#5C5C59] text-xs mb-1">Datum</div><div className="font-medium">{formatDate(date)}</div></div>
        <div>
          <div className="text-[#5C5C59] text-xs mb-1">Zeitrahmen</div>
          <div className="font-medium font-mono" data-testid="summary-time-range">
            {slot?.time}{summaryDuration ? <> – {addMinutesToHm(slot.time, summaryDuration)}</> : null}
          </div>
        </div>
        <div>
          <div className="text-[#5C5C59] text-xs mb-1">Aufenthalt</div>
          <div className="font-medium font-mono">{summaryDuration ? `${summaryDuration} Min.` : '–'}</div>
        </div>
        <div><div className="text-[#5C5C59] text-xs mb-1">Personen</div><div className="font-medium">{persons}</div></div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#1C1C19] mb-2">Name *</label>
          <input
            type="text"
            value={form.customer_name}
            onChange={e => setForm({ ...form, customer_name: e.target.value })}
            data-testid="contact-name-input"
            className="w-full px-4 py-3 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19] transition"
            placeholder="Max Mustermann"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1C1C19] mb-2">Telefon *</label>
          <input
            type="tel"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            data-testid="contact-phone-input"
            className="w-full px-4 py-3 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19] transition"
            placeholder="+49 ..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1C1C19] mb-2">E-Mail<span className="text-[#EF4444]"> *</span></label>
          <input
            type="email"
            required
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            data-testid="contact-email-input"
            className="w-full px-4 py-3 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19] transition"
            placeholder="max@example.com"
          />
          <p className="text-xs text-[#5C5C59] mt-1">Für die Bestätigung Ihrer Reservierung — wir verwenden Ihre E-Mail nur für die Reservierungsbestätigung.</p>
        </div>

        {/* Tag groups (replaces the old free-text Bemerkung field) */}
        {orderedCats.length > 0 && (
          <div className="pt-2" data-testid="tag-groups">
            <p className="text-sm font-medium text-[#1C1C19] mb-1">Besondere Wünsche (optional)</p>
            <p className="text-xs text-[#5C5C59] mb-3">Wählen Sie alle zutreffenden Punkte aus.</p>
            <div className="space-y-4">
              {orderedCats.map(cat => (
                <div key={cat} data-testid={`tag-category-${cat}`}>
                  <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-2">{CATEGORY_LABELS[cat] || cat}</p>
                  <div className="flex flex-wrap gap-2">
                    {grouped[cat].map(t => {
                      const sel = selectedTagIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => toggleTag(t.id)}
                          data-testid={`tag-option-${t.id}`}
                          className={`px-3 py-2 rounded-full text-sm border transition flex items-center gap-2 ${
                            sel
                              ? 'border-[#1C1C19] bg-[#1C1C19] text-white'
                              : 'border-[#E5E5E0] bg-white text-[#1C1C19] hover:border-[#1C1C19]'
                          }`}
                          style={sel ? { background: t.color || '#1C1C19', borderColor: t.color || '#1C1C19' } : {}}
                        >
                          {t.icon && <span className="text-base leading-none">{t.icon}</span>}
                          <span>{t.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pre-order offers (Sekt, Vorspeisen, Menüs) */}
        {availOffers.length > 0 && (
          <div className="pt-2" data-testid="offer-section">
            <p className="text-sm font-medium text-[#1C1C19] mb-1">Vorbestellung (optional)</p>
            <p className="text-xs text-[#5C5C59] mb-3">Möchten Sie etwas vorbestellen? Bereiten wir gern für Sie vor.</p>
            <div className="space-y-2">
              {availOffers.map(o => {
                const qty = offerQty[o.id] || 0;
                if (o.per_person) {
                  const selected = qty > 0;
                  const lineTotal = persons * (Number(o.price) || 0);
                  return (
                    <div key={o.id} data-testid={`offer-item-${o.id}`}
                      className={`flex items-center gap-3 p-3 rounded border transition ${selected ? 'border-[#1C1C19] bg-[#1C1C19]/5' : 'border-[#E5E5E0] bg-white'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#1C1C19]">{o.name}{Number(o.price) > 0 && <span className="text-[#5C5C59] font-normal"> · {fmtPrice(o.price)} pro Person</span>}</div>
                        {o.description && <div className="text-xs text-[#5C5C59] mt-0.5">{o.description}</div>}
                        {selected && Number(o.price) > 0 && <div className="text-xs text-[#5C5C59] mt-0.5">{persons} × {fmtPrice(o.price)} = <span className="font-medium text-[#1C1C19]">{fmtPrice(lineTotal)}</span></div>}
                      </div>
                      <button type="button" onClick={() => setQty(o.id, selected ? 0 : persons)}
                        data-testid={`offer-toggle-${o.id}`}
                        className={`px-3 py-2 rounded-full text-sm border transition shrink-0 ${selected ? 'border-[#1C1C19] bg-[#1C1C19] text-white' : 'border-[#E5E5E0] bg-white text-[#1C1C19] hover:border-[#1C1C19]'}`}>
                        {selected ? 'Entfernen' : `Für ${persons} Pers.`}
                      </button>
                    </div>
                  );
                }
                return (
                  <div key={o.id} data-testid={`offer-item-${o.id}`}
                    className={`flex items-center gap-3 p-3 rounded border transition ${qty > 0 ? 'border-[#1C1C19] bg-[#1C1C19]/5' : 'border-[#E5E5E0] bg-white'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1C1C19]">{o.name}{Number(o.price) > 0 && <span className="text-[#5C5C59] font-normal"> · {fmtPrice(o.price)}</span>}</div>
                      {o.description && <div className="text-xs text-[#5C5C59] mt-0.5">{o.description}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" onClick={() => setQty(o.id, qty - 1)} disabled={qty <= 0}
                        data-testid={`offer-minus-${o.id}`} aria-label="Weniger"
                        className="w-8 h-8 rounded-full border border-[#E5E5E0] text-[#1C1C19] hover:border-[#1C1C19] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-lg leading-none">−</button>
                      <span data-testid={`offer-qty-${o.id}`} className="w-6 text-center font-mono text-sm tabular-nums">{qty}</span>
                      <button type="button" onClick={() => setQty(o.id, qty + 1)}
                        data-testid={`offer-plus-${o.id}`} aria-label="Mehr"
                        className="w-8 h-8 rounded-full border border-[#E5E5E0] text-[#1C1C19] hover:border-[#1C1C19] flex items-center justify-center text-lg leading-none">+</button>
                    </div>
                  </div>
                );
              })}
            </div>
            {offerTotal > 0 && (
              <div className="text-right text-sm text-[#5C5C59] mt-2" data-testid="offer-total">
                Summe Vorbestellung: <span className="font-medium text-[#1C1C19]">{fmtPrice(offerTotal)}</span>
              </div>
            )}
          </div>
        )}

        {/* Optional free-text note (only if admin enabled it) */}
        {notesEnabled && (
          <div data-testid="optional-note-field">
            <label className="block text-sm font-medium text-[#1C1C19] mb-2">
              Weitere Hinweise (optional)
              <span className="text-xs text-[#5C5C59] font-normal ml-2">{(form.note || '').length}/{notesMaxChars}</span>
            </label>
            <textarea
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value.slice(0, notesMaxChars) })}
              data-testid="contact-note-input"
              rows={3}
              maxLength={notesMaxChars}
              className="w-full px-4 py-3 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19] transition resize-none"
              placeholder="Falls Sie uns zusätzlich etwas mitteilen möchten..."
            />
          </div>
        )}
      </div>

      {/* GDPR consent — only shown when the admin marks it as required */}
      {privacyRequired && (
        <div className="mt-6 p-4 bg-[#F9F9F7] border border-[#E5E5E0] rounded" data-testid="privacy-consent-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              data-testid="privacy-checkbox"
              className="mt-1 w-4 h-4 accent-[#1C1C19] cursor-pointer flex-shrink-0"
            />
            <span className="text-xs text-[#1C1C19] leading-relaxed">
              Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" data-testid="privacy-link-inline" className="underline hover:text-[#3A3A35]">Datenschutzhinweise</a> gelesen und akzeptiere die Verarbeitung meiner Daten zur Bearbeitung meiner Reservierung.
              <span className="text-[#EF4444] ml-0.5">*</span>
            </span>
          </label>
        </div>
      )}

      <button
        onClick={onSubmit}
        disabled={submitting || (privacyRequired && !privacyAccepted)}
        data-testid="submit-reservation-btn"
        className="btn-primary w-full py-4 rounded font-medium mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Wird gesendet...' : 'Reservierung absenden'}
      </button>
      <p className="text-xs text-[#5C5C59] mt-4 text-center">Mit dem Absenden bestätigen Sie, dass Sie unsere Reservierungsbedingungen akzeptieren.</p>
    </div>
  );
}

function WaitlistDialog({ open, onClose, date, persons, defaultTime, areas, preferredArea, openingHours, specialDays, settings }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: '', phone: '', email: '',
    preferred_time: defaultTime || '',
    accepts_alternative_times: true,
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Compute valid time options strictly within opening hours of the chosen day.
  // Uses 15-min steps and excludes the closing minute (no point starting at close).
  const timeOptions = validTimeOptionsForDate(date, openingHours || [], specialDays || [], 15, settings);

  useEffect(() => {
    if (open) {
      // Pick a sensible default: incoming default, otherwise first opening slot.
      setForm(f => ({
        ...f,
        preferred_time: (defaultTime && timeOptions.includes(defaultTime))
          ? defaultTime
          : (timeOptions[0] || ''),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTime, date, openingHours, specialDays]);

  if (!open) return null;

  const submit = async () => {
    if (!form.customer_name || !form.phone) {
      toast.error('Bitte Name und Telefon angeben');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/waitlist', {
        ...form,
        date,
        persons,
        preferred_area_id: preferredArea || null,
      });
      toast.success('Sie sind auf der Warteliste – wir melden uns, sobald ein Tisch frei wird.');
      onClose();
      navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="waitlist-dialog">
        <h2 className="font-display text-2xl mb-2">Auf Warteliste setzen</h2>
        <p className="text-sm text-[#5C5C59] mb-4">{formatDate(date)} · {persons} {persons === 1 ? 'Person' : 'Personen'}</p>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#1C1C19] mb-1">Name *</label>
            <input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} data-testid="wl-name" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1C1C19] mb-1">Telefon *</label>
            <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="wl-phone" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1C1C19] mb-1">E-Mail (optional)</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="wl-email" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1C1C19] mb-1">Wunschuhrzeit</label>
            {timeOptions.length > 0 ? (
              <select
                value={form.preferred_time}
                onChange={e => setForm({...form, preferred_time: e.target.value})}
                data-testid="wl-time"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19] bg-white"
              >
                {timeOptions.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            ) : (
              <div className="px-3 py-2 border border-dashed border-[#E5E5E0] rounded text-sm text-[#5C5C59]" data-testid="wl-time-closed">
                An diesem Tag hat das Restaurant geschlossen.
              </div>
            )}
            <p className="text-xs text-[#5C5C59] mt-1">Auswahl nur innerhalb der Öffnungszeiten.</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.accepts_alternative_times} onChange={e => setForm({...form, accepts_alternative_times: e.target.checked})} data-testid="wl-accept-alt" />
            Auch alternative Uhrzeiten akzeptieren
          </label>
          <div>
            <label className="block text-sm font-medium text-[#1C1C19] mb-1">Bemerkung (optional)</label>
            <textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})} data-testid="wl-note" rows={2} className="w-full px-3 py-2 border border-[#E5E5E0] rounded resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} data-testid="wl-cancel" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19]">Abbrechen</button>
          <button onClick={submit} disabled={submitting || !form.preferred_time} data-testid="wl-submit" className="bg-[#1C1C19] hover:bg-[#3A3A35] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm">
            {submitting ? 'Sende...' : 'Auf Warteliste setzen'}
          </button>
        </div>
      </div>
    </div>
  );
}
